/**
 * MediaPanelContent - New rendering component using slot-based architecture
 *
 * This component replaces PanelBody and uses the new layout system:
 * - Reads SlotRegistry to determine control placement
 * - Renders controls based on location (content-header, content-footer, etc.)
 * - Uses GridData for tile rendering
 *
 * Benefits:
 * - Single source of truth (slots from layout strategies)
 * - No hardcoded control logic
 * - Extensible (easy to add new controls)
 * - Testable (pure rendering from props)
 */

import {MediaCardType} from '@wandb/weave/common/types/media';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React from 'react';
import {twMerge} from 'tailwind-merge';

import {CustomRunNames} from '../../state/views/customRunNames/types';
import * as PanelTypes from '../../state/views/panel/types';
import {PartRefFromObjSchema} from '../../state/views/types';
import {KeyTypes} from './common';
import {
  MediaBrowserPanelProps,
  MediaConfigWithDefaults,
  SizingSettings,
} from './Component';
import {Pagination} from './controls';
import {GalleryRenderer, GridRenderer} from './layout/renderers';
import {ContentFooterSlot} from './layout/slots/ContentFooterSlot';
import {ContentHeaderSlot} from './layout/slots/ContentHeaderSlot';
import {GridData, SlotRegistry} from './layout/types';
import {PanelHeader} from './PanelHeader';
import {PanelSettings} from './PanelSettings';
import {TilePagination} from './tiles';
import {StepMeta} from './types';
import {getTitleFromConfig} from './utils/getTitleFromConfig';

export type MediaPanelContentProps = {
  gridData: GridData;
  slots: SlotRegistry;
  config: MediaConfigWithDefaults;
  updateConfig: MediaBrowserPanelProps['updateConfig'];
  stepMeta: StepMeta;
  currentStep: number;
  mediaIndex: number;
  isMaxStepLoading: boolean;
  mediaBrowserSize: {
    width: number;
    height: number;
  };
  cardWidth: number;
  cardHeight: number;
  mediaType: MediaCardType;
  mediaSliderKey: string;
  panelRef: PartRefFromObjSchema<PanelTypes.PanelObjSchema>;
  disableRunLinks?: boolean;
  customRunNames?: CustomRunNames;
  columnCount: number;
  shrunk?: boolean;
  showActualSize?: boolean;
  mediaBrowserRefCallback: (el: HTMLDivElement) => void;
  panelViewerRef: React.MutableRefObject<HTMLDivElement | null>;
  tilePagination: TilePagination | null;
  allMasks?: any;
  // Config mode props
  configMode?: boolean;
  sizingSettings: SizingSettings;
  allMediaKeys: string[];
  keyTypes: KeyTypes;
  numericKeys?: string[];
};

export const MediaPanelContent: React.FC<MediaPanelContentProps> = ({
  gridData,
  slots,
  config,
  updateConfig,
  stepMeta,
  currentStep,
  mediaIndex,
  isMaxStepLoading,
  mediaBrowserSize,
  cardWidth,
  cardHeight,
  mediaType,
  mediaSliderKey,
  panelRef,
  disableRunLinks,
  customRunNames,
  columnCount,
  shrunk,
  showActualSize,
  mediaBrowserRefCallback,
  panelViewerRef,
  tilePagination,
  allMasks,
  configMode = false,
  sizingSettings,
  allMediaKeys,
  keyTypes,
  numericKeys,
}) => {
  // Extract slot locations
  const contentHeaderControls = slots['content-header'] || [];
  const contentFooterControls = slots['content-footer'] || [];

  const chartTitle = getTitleFromConfig(config);

  const content = (
    <div className="flex h-full flex-col">
      {/* Panel Header - Chart Title */}
      {chartTitle && <PanelHeader config={{...config, chartTitle}} />}

      {/* Content Header - Full width controls above content */}
      {(contentHeaderControls.length > 0 || tilePagination) && (
        <div className="content-header shrink-0 flex items-center justify-between mb-6">
          <div className="flex-1">
            {contentHeaderControls.length > 0 && (
              <ContentHeaderSlot
                controls={contentHeaderControls}
                config={config}
                updateConfig={updateConfig}
                stepMeta={stepMeta}
                currentStep={currentStep}
                mediaIndex={mediaIndex}
              />
            )}
          </div>
          {tilePagination && (
            <div className="ml-auto">
              <Pagination
                panelViewerRef={panelViewerRef}
                updateConfig={updateConfig}
                tilePagination={tilePagination}
              />
            </div>
          )}
        </div>
      )}

      {/* Main Content Area */}
      <div
        className={twMerge(
          'h-full overflow-hidden',
          'flex flex-col',
          'max-h-[calc(100vh-60px-55px-57px-1px-12px-88px-30px-12px-1px)]',
          'md:max-h-[calc(100vh-60px-57px-1px-12px-56px-30px-12px-1px)]'
        )}>
        <div
          className="panel-media shrink overflow-auto"
          ref={mediaBrowserRefCallback}>
          <div ref={panelViewerRef} className="panel-viewer">
            {config.mode === 'gallery' ? (
              <GalleryRenderer
                gridData={gridData}
                slots={slots}
                config={config}
                cardWidth={cardWidth}
                cardHeight={cardHeight}
                mediaType={mediaType}
                mediaSliderKey={mediaSliderKey}
                panelRef={panelRef}
                disableRunLinks={disableRunLinks}
                customRunNames={customRunNames}
                columnCount={columnCount}
                shrunk={shrunk}
                showActualSize={showActualSize}
                allMasks={allMasks}
                tilePagination={tilePagination}
              />
            ) : (
              <GridRenderer
                gridData={gridData}
                slots={slots}
                config={config}
                cardWidth={cardWidth}
                cardHeight={cardHeight}
                mediaType={mediaType}
                mediaSliderKey={mediaSliderKey}
                panelRef={panelRef}
                disableRunLinks={disableRunLinks}
                customRunNames={customRunNames}
                columnCount={columnCount}
                shrunk={shrunk}
                showActualSize={showActualSize}
                allMasks={allMasks}
              />
            )}
          </div>
        </div>

        {/* Content Footer - Full width controls below content */}
        {contentFooterControls.length > 0 && (
          <div className="shrink-0">
            <ContentFooterSlot
              controls={contentFooterControls}
              config={config}
              updateConfig={updateConfig}
              stepMeta={stepMeta}
              currentStep={currentStep}
              mediaIndex={mediaIndex}
              isMaxStepLoading={isMaxStepLoading}
              mediaBrowserSize={mediaBrowserSize}
              gridData={gridData}
              columnCount={columnCount}
            />
          </div>
        )}
      </div>
    </div>
  );

  // Render with config mode sidebar if enabled
  if (configMode) {
    return (
      <Tailwind>
        <div className="panel-media__config-container">
          <div className="panel-media__config-content">{content}</div>
          <div className="panel-media__config-settings">
            <PanelSettings
              mediaType={mediaType}
              sizingSettings={sizingSettings}
              allMediaKeys={allMediaKeys}
              numericKeys={numericKeys}
              config={config}
              updateConfig={updateConfig}
              keyTypes={keyTypes}
            />
          </div>
        </div>
      </Tailwind>
    );
  }

  return (
    <div data-test="media-panel" className="panel-media__container">
      {content}
    </div>
  );
};

export default MediaPanelContent;
